// ============================================================
// installations.nav.ts — pilot navigation descriptor
// ------------------------------------------------------------
// Component-free, data-only navigation descriptor for the
// Installations feature + its 8 detail tabs. Imports ONLY pure
// types/constants (@twist/ui-navigation types + @twist/core security
// primitives) — no component / feature code.
//
// Mirrors the legacy chrome EXACTLY so nothing tightens or loosens:
//  - Sidebar: legacy "My Twist" section → Installations leaf, icon
//    `installations`, no extra security (route is authenticated-only).
//  - Tabs: the 8 tabs from InstallationTabNavigationComponent in order
//    (Metadata stays commented out — NOT included). Firmware is gated
//    by TWIST_ADMIN_ONLY, identical to the legacy tab strip.
// ============================================================
import { TWIST_ADMIN_ONLY } from '@twist/core';
import type { NavDescriptor } from '@twist/ui-navigation';

export const installationsNav: NavDescriptor = {
  id: 'installations',
  path: '/installations',
  label: 'Installations',
  icon: 'installations',
  group: 'My Twist',
  // Section + item ordering (My Twist = 10s). The group's section
  // position is derived from its lowest item order, so this single
  // `order` both places Installations first in My Twist AND ranks the
  // My Twist section ahead of Organization/Analytics/Admin.
  order: 10,
  surfaces: ['sidebar', 'palette'],
  // No extra security — the /installations route is authenticated-only
  // and the legacy "My Twist" Installations leaf carried no constraints.
  children: [
    { id: 'installation-dashboard', path: 'dashboard', label: 'Dashboard', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 1 },
    { id: 'installation-products', path: 'products', label: 'Products', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 2 },
    { id: 'installation-scenes', path: 'scenes', label: 'Scenes', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 3 },
    { id: 'installation-routines', path: 'routines', label: 'Routines', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 4 },
    {
      id: 'installation-firmware',
      path: 'firmware',
      label: 'Firmware',
      surfaces: ['tabs', 'breadcrumb', 'palette'],
      order: 5,
      // TWIST_ADMIN_ONLY = { orgTypes: [TwistAdmin] } — identical to the
      // legacy Firmware tab gate.
      security: TWIST_ADMIN_ONLY,
    },
    { id: 'installation-security', path: 'security', label: 'Security', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 6 },
    { id: 'installation-diagnostics', path: 'diagnostics', label: 'Diagnostics', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 7 },
    { id: 'installation-programming', path: 'programming', label: 'Programming', surfaces: ['tabs', 'breadcrumb', 'palette'], order: 8 },
  ],
};
